// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Types
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { AuthenticationType } from "@Settings/types/Common/auth";
import { GlobalPositioningServiceFragment, KeyManagementSystemFragment } from "@Settings/api/AdministrationTools/fragments";
import { IAdministrationTools } from "@Settings/types/administrationTools/model";
import { Nullable, Nullish } from "@Common/interfaces/types";
import { EManagementSystem, IRequestClientAccessResponse, TGpsStore, TOneMicroStore } from "@/Settings/types/administrationTools/utils";
import { IInventoryStatusList, ISystemTenant, TDataSource } from "@/Settings/types/administrationTools/dealerManagementSystem";

export async function getAdministrationTools(locationId: string) {
	const query = gql`
		query getAdministrationTools($locationId: String!) {
			getAdministrationTools(locationId: $locationId) {
				_id
				locationId
				integrations {
					dmsProvider {
						providerName
						provider
						systemTenantId
						dataSource
						userID
						storeNumber
						areaNumber
						hasDealerNumber
					}
					dmsSources {
						_id
						label
						value
					}
					dmsControlTypes {
						identifier
						label
						value
					}
					generalLedgerPrefix {
						_id
						oemName
						oemReference
						prefixes
					}
					reputationManagement {
						provider
						commissionCycleLastUpdate {
							intendedCycle
							dayOfTheMonth
						}
						provider
					}
					opportunityMining {
						_id
						provider {
							_id
							providerName
							configuration {
								associationWindow
							}
						}
					}
					reynoldsInventoryMapping {
						new {
							name
							mappedName
							isEditable
							isActive
							isCustom
							isEditable
						}
						preOwned {
							name
							mappedName
							isEditable
							isActive
							isCustom
						}
					}
					globalPositioningService {
						...GlobalPositioningService
					}
					keyManagementSystem {
						...KeyManagementSystem
					}
					customerRelationshipManagement {
						providerName
						provider
						systemTenantId
						dataSource
						hasDealerNumber
						storeNumber
						areaNumber
					}
					phoneServiceProvider {
						providerName
					}
					defaultTradeAmount {
						newVehicle {
							reference
							modifier {
								value
								type
							}
						}
						preOwnedVehicle {
							reference
							modifier {
								value
								type
							}
						}
					}
					customerRelationshipManagement {
						providerName
						provider
						systemTenantId
						dataSource
						hasDealerNumber
						storeNumber
						areaNumber
					}
					payrollProviders {
						providerName
						configuredAccounts {
							companyId
							displayId
							status
							statusDate
						}
					}
				}
			}
		}
		${GlobalPositioningServiceFragment}
		${KeyManagementSystemFragment}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getAdministrationTools", IAdministrationTools> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		const data = response.data.data?.getAdministrationTools;

		// Transform the data to ensure configuredAccounts is always an array
		if (data?.integrations?.payrollProviders) {
			data.integrations.payrollProviders = data.integrations.payrollProviders.map((provider) => ({
				...provider,
				configuredAccounts: provider.configuredAccounts || [],
			}));
		}

		return data;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getAdministrationTools", message: "Error getting administration tools", variables });
		return null;
	}
}

type TProviderRole = {
	role: string;
	authType: AuthenticationType;
	properties: string;
};

export type TGetProviderList = {
	_id: string;
	provider: string;
	providerName: string;
	providerDBProperty: string;
	auth: {
		roles: TProviderRole[];
	};
	imageUrl: string;
};
export async function getProviderList(locationId: string, categoryKey: string): Promise<Nullish<TGetProviderList[]>> {
	const query = gql`
		query getProviderList($locationId: String!, $categoryKey: String!) {
			getProviderList(locationId: $locationId, categoryKey: $categoryKey) {
				_id
				provider
				providerName
				providerDBProperty
				auth {
					roles {
						role
						authType
						properties
					}
					baseUrl
					endpoint
					httpMethod
				}
				imageUrl
			}
		}
	`;

	const variables = {
		locationId,
		categoryKey,
	};

	try {
		const response: GraphqlQueryResponse<"getProviderList", TGetProviderList[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getProviderList ?? null;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getProviderList", message: "Error getting provider list", variables });
		return null;
	}
}

export async function getMetaLastVersionOemsInfo(locationId: string) {
	const query = gql`
		query getOemMetaLastVersion($locationId: String!) {
			getOemMetaLastVersion(locationId: $locationId) {
				_id
				oemReference
				lastVersion {
					version
					oemShort
					oemName
					metrics
					_id
					financialStatements {
						version
						_id
						franchises {
							_id
							franchise
							programs {
								program
								metrics {
									_id
									metricName
									metricKey
								}
							}
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getOemMetaLastVersion;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getMetaLastVersionOemsInfo", message: "Error getting OEMs info", variables });
		return null;
	}
}

export async function getStorePresignedUrl(locationId: string, pictureName: string) {
	const query = gql`
		query getPresignedStorePicture($locationId: String!, $pictureName: String!) {
			getPresignedStorePicture(locationId: $locationId, pictureName: $pictureName) {
				presignedUrl
			}
		}
	`;

	const variables = {
		locationId,
		pictureName,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getPresignedStorePicture;
	} catch (err) {
		errorLogger(err as Error, {
			functionName: "getPresignedStorePicture",
			message: "Error getting presigned store picture",
			variables,
		});
		return null;
	}
}

export async function getRegionMetaLastVersion(locationId: string, oemReference: string, franchise: string) {
	const query = gql`
		query getRegionMetaLastVersion($locationId: String!, $oemReference: String!, $franchise: String!) {
			getRegionMetaLastVersion(locationId: $locationId, oemReference: $oemReference, franchise: $franchise) {
				oemReference
				franchise {
					version {
						_id
						version
						regions {
							_id
							regionKey
							regionName
							regionShort
							markets {
								_id
								marketAreas
								version
							}
						}
					}
					franchiseName
				}
			}
		}
	`;

	const variables = {
		locationId,
		oemReference,
		franchise,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getRegionMetaLastVersion;
	} catch (err) {
		errorLogger(err as Error, {
			functionName: "getRegionMetaLastVersion",
			message: "Error getting region meta last version",
			variables,
		});
		return null;
	}
}

export async function getInventoryStatusList(provider: string): Promise<Nullable<IInventoryStatusList>> {
	const query = gql`
		query getInventoryStatusList($provider: String!) {
			getInventoryStatusList(provider: $provider) {
				_id
				provider
				statusesList {
					_id
					title
					statuses {
						_id
						isEditable
						status
					}
				}
				mappingOptions {
					vehicleType
					options {
						_id
						status
						label
					}
				}
			}
		}
	`;

	const variables = {
		provider,
	};

	try {
		const response: GraphqlQueryResponse<"getInventoryStatusList", IInventoryStatusList> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getInventoryStatusList ?? null;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getInventoryStatusList", message: "Error getting inventory status list", variables });
		return null;
	}
}

export async function getGpsStores(locationId: string, providerDBProperty: string): Promise<Nullable<TGpsStore[]>> {
	const query = gql`
		query getGpsStores($locationId: String!, $providerDBProperty: String!) {
			getGpsStores(locationId: $locationId, providerDBProperty: $providerDBProperty) {
				gpsStoreId
				gpsStoreName
			}
		}
	`;

	const variables = {
		locationId,
		providerDBProperty,
	};

	try {
		const response: GraphqlQueryResponse<"getGpsStores", TGpsStore[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getGpsStores;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getGpsStores", message: "Error getting GPS stores", variables });
		return null;
	}
}

export const getOemMetaData = async ({ locationId, countryCode }: { locationId: string; countryCode: string }) => {
	const query = gql`
		query getOemMetaData($countryCode: String!, $locationId: String!) {
			getOemMetaData(countryCode: $countryCode, locationId: $locationId) {
				_id
				financialStatementVersion
				oemName
				oemReference
				version
				franchisesNames
				franchises {
					_id
					franchise
					areas
				}
			}
		}
	`;

	const variables = {
		locationId,
		countryCode,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });

		return response.data.data?.getOemMetaData;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getOemMetaData", variables });
		return null;
	}
};

export const getDataSourceList = async (locationId: string, categoryKey: string, providerName: string) => {
	const query = gql`
		query getDataSourceList($locationId: String!, $categoryKey: String!, $providerName: String!) {
			getDataSourceList(locationId: $locationId, categoryKey: $categoryKey, providerName: $providerName) {
				_id
				value
				label
			}
		}
	`;

	const variables = {
		locationId,
		categoryKey,
		providerName,
	};

	try {
		const response: GraphqlQueryResponse<"getDataSourceList", TDataSource[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getDataSourceList;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getDataSourceList", message: "Error getting provider list", variables });
		return null;
	}
};

export const getSystemTenant = async (args: { locationId: string; systemType: string; provider: string }) => {
	const query = gql`
		query getSystemTenant($locationId: String!, $systemType: String!, $provider: String!) {
			getSystemTenant(locationId: $locationId, systemType: $systemType, provider: $provider) {
				_id
				providerName
				systemTenantName
				systemTenantId
				tenantId
				isSilo
			}
		}
	`;

	const variables = args;

	try {
		const response: GraphqlQueryResponse<"getSystemTenant", ISystemTenant> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSystemTenant ?? null;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getSystemTenant", message: "Error getting the System Tenant", variables });
		return null;
	}
};

export const getDisabledAreaNumbersByStoreNumber = async (
	locationId: string,
	storeNumber: string,
	managementSystemType: EManagementSystem
): Promise<Nullish<string[]>> => {
	const query = gql`
		query getDisabledAreaNumbersByStoreNumber($locationId: String!, $storeNumber: String!, $managementSystemType: String!) {
			getDisabledAreaNumbersByStoreNumber(
				locationId: $locationId
				storeNumber: $storeNumber
				managementSystemType: $managementSystemType
			)
		}
	`;

	const variables = {
		locationId,
		storeNumber,
		managementSystemType,
	};

	try {
		const response: GraphqlQueryResponse<"getDisabledAreaNumbersByStoreNumber", string[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getDisabledAreaNumbersByStoreNumber ?? null;
	} catch (err) {
		errorLogger(err as Error, {
			functionName: "getDisabledAreaNumbersByStoreNumber",
			message: "Error getting disabled area numbers",
			variables,
		});
		return null;
	}
};

export async function getKmsStores(locationId: string, provider: string): Promise<Nullable<TOneMicroStore[]>> {
	const query = gql`
		query GetKmsStores($locationId: String!, $provider: String!) {
			getKmsStores(locationId: $locationId, provider: $provider) {
				id
				name
				disabled
				kiosks {
					id
					name
				}
			}
		}
	`;

	const variables = {
		locationId,
		provider,
	};

	try {
		const response: GraphqlQueryResponse<"getKmsStores", TOneMicroStore[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getKmsStores;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getKmsStores", message: "Error getting KMS stores", variables });
		return null;
	}
}

export async function getRequestClientAccess(
	locationId: string,
	providerName: string,
	displayId: string
): Promise<IRequestClientAccessResponse | null> {
	const query = gql`
		query getRequestClientAccess($locationId: String!, $providerName: String!, $displayId: String!) {
			getRequestClientAccess(locationId: $locationId, providerName: $providerName, displayId: $displayId) {
				providerName
				approvalLink
				error
				errorMessage
			}
		}
	`;

	const variables = {
		locationId,
		providerName,
		displayId,
	};

	try {
		const response: GraphqlQueryResponse<"getRequestClientAccess", IRequestClientAccessResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getRequestClientAccess ?? null;
	} catch (err) {
		errorLogger(err as Error, { functionName: "getRequestClientAccess", variables });
		return null;
	}
}
